import { get } from 'lodash';
import join from 'lodash/fp/join';
import { InMemoryWebStorage, Log, User, UserManager, WebStorageStateStore } from 'oidc-client';
import { OAuthConfigSettings } from 'src/main';

import { configureEnv, env } from '../../env';
import { configureStorage, RouteStorage } from './storage';
import { AuthenticatedUserWithProfile, MappedUserProfile, mapUserProfile, OICDUserProfile } from './userProfile';

const trace = env.featureToggles.tracing
    ? // eslint-disable-next-line no-console
      (...args: unknown[]) => console.log(`[oidcLogin]`, ...args)
    : () => {};

const getWindow = () => (typeof window === 'undefined' ? ({} as Window & typeof globalThis) : window);

const param = (window: Window, regex: RegExp, defaultValue: string | null = null) => {
    let result = defaultValue;
    decodeURI(window.location.href).replace(regex, (_, it: string) => {
        result = it;
        return result;
    });
    return result;
};

// For some reason this creates problems with typescript when using getAccount function
// const pullLocale = getOr('en-GB', 'profile.locale');
const pullLocale = (result: AuthenticatedUserWithProfile) => get(result, 'profile.locale', 'en-GB');

export const REQUIRED_SCOPE = 'perform.read';

export const hasRequiredScope = (scopes: string[]): boolean => scopes.includes(REQUIRED_SCOPE);

export type UserPublishedInfo = {
    accessToken: string;
    expiresInSeconds: number;
    idToken: OICDUserProfile;
    locale: string;
    profile: MappedUserProfile;
    scopes: string[];
};

const adaptPublishedInfo = (result: AuthenticatedUserWithProfile): UserPublishedInfo => {
    return {
        accessToken: result.access_token,
        expiresInSeconds: result.expires_in,
        idToken: result.profile,
        locale: pullLocale(result),
        profile: mapUserProfile(result.profile),
        scopes: result.scope ? result.scope.split(' ') : [],
    };
};

const configureAuth = (window: Window & typeof globalThis, importMetaEnv: ImportMetaEnv) => {
    const { runtimeConfig } = configureEnv(window, importMetaEnv);

    const redirectUri = runtimeConfig.login.redirectUri;
    const silentRedirectUri = runtimeConfig.login.silentRedirectUri;

    const settings = {
        authority: `${runtimeConfig.login.authority}`,
        client_id: `${runtimeConfig.login.clientId}`,
        client_secret: `${runtimeConfig.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: `code`,
        scope: join(' ', runtimeConfig.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    };

    trace('oidc.auth.settings', settings);

    return new UserManager(settings);
};

export const configureSetupOAuth = (
    auth: UserManager,
    storage: RouteStorage,
    window: Window & typeof globalThis,
    importMetaEnv: ImportMetaEnv
) => {
    const { featureToggles, runtimeConfig } = configureEnv(window, importMetaEnv);

    if (featureToggles.tracing) {
        Log.logger = console;

        Log.level = Log.DEBUG;
    }

    const isFreshRedirect = Boolean(param(window, /.*code=([^&]+)/u));

    const saveCurrentRoute = () => {
        const initialRoute = [window.location.hash, window.location.search].join('').replace(/^#/u, '');

        storage.saveRoute(initialRoute);

        trace('saving initial route', initialRoute);
    };

    return (config: OAuthConfigSettings) => {
        const trySignin = () =>
            auth
                .signinSilent()
                .then((result: User) => {
                    trace('oidc.signinSilent success!', result);
                    config.onTokenRenewed(adaptPublishedInfo(result));

                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    return result;
                })
                .catch((error: Error) => {
                    trace('oidc.signinSilent failed', error);

                    if (!isFreshRedirect) {
                        saveCurrentRoute();
                    }

                    config.onTokenExpired();
                    return Promise.reject(error);
                });

        auth.events.addAccessTokenExpiring((...args) => {
            trace('oidc.accessTokenExpiring', ...args);
            trace('  triggering manual silent renewal...');

            saveCurrentRoute();
            return trySignin();
        });

        auth.events.addAccessTokenExpired((...args) => {
            trace('oidc.accessTokenExpired', ...args);
            config.onTokenExpired();
        });

        auth.events.addSilentRenewError(error => {
            trace('oidc.silentRenewError', error);
            config.onSessionError(error);
            config.onTokenExpired();
        });

        auth.events.addUserLoaded((...args) => {
            trace('oidc.userLoaded', ...args);
        });

        auth.events.addUserSignedOut((...args) => {
            trace('oidc.userSignedOut', ...args);
            config.onTokenExpired();
        });

        return trySignin().catch(error => {
            trace('oidc.signinSilent failed, trying page redirect...', error);

            const mightBeSuspicious = isFreshRedirect;

            if (runtimeConfig.login.preventRedirect) {
                // eslint-disable-next-line no-console
                console.warn('[feature/login] redirect prevented due to config. Error was', error);
            } else if (mightBeSuspicious) {
                trace('oidc.signinSilent.error', 'redirect prevented due to supsicious signin error', error);
                storage.discardRoute();
                config.onSessionError(error);
            } else {
                saveCurrentRoute();
                auth.signinRedirect();
            }

            return Promise.reject(new Error(`Need to sign in`));
        });
    };
};

const runtimeAuth = configureAuth(window, import.meta.env);

if (env.featureToggles.tracing) {
    trace(`runtimeAuth`, runtimeAuth);
}

export const setupOAuth = configureSetupOAuth(runtimeAuth, configureStorage(getWindow()), getWindow(), import.meta.env);

export const mockOAuth = ({ onTokenRenewed }: { onTokenRenewed: (result: UserPublishedInfo) => unknown }) => {
    // eslint-disable-next-line no-console
    console.warn(`[feature/login/oidc-session] Using mocked authorization due to config setting`);

    onTokenRenewed(
        adaptPublishedInfo({
            access_token: (env.featureToggles.mockedToken as string) || 'valid-mocked-oauth-bogus-token',
            expires_in: 60 * 60 * 24 * 365,
            scope: `openid ${REQUIRED_SCOPE}`,
            profile: {
                locale: env.runtimeConfig.login.mockLocale,
                account: '4c8e1f07-8510-4bbf-79ca-9d706675b9da',
            },
        })
    );

    return Promise.resolve();
};

export const configureRetrieveInitialState = (storage: RouteStorage) => () => ({
    initialRoute: storage.getRoute(),
});

export const retrieveInitialState = configureRetrieveInitialState(configureStorage(getWindow()));
